import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';

import React, { useState, useCallback, useContext, useEffect, useMemo } from 'react';
import classnames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { Button } from 'ui-components/components/button';
import { Input, version2 } from 'ui-components/components/input';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { Text, TextType } from 'ui-components/components/text';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';

import { useEnablePudoDeliveryOptions } from '../../../../../../feature-flags/hooks/use-enable-pudo-delivery-options';

import { useAreLocalDeliveryServicesAvailableForSeller } from '../../../../../../hooks/use-are-local-delivery-services-available-for-seller';
import { useYupFormValidation, ERROR } from '../../../../../../validation/use-yup-form-validation';
import HelpArticleLink from '../../../../../../components/help-article-link';
import { linkIDs } from '../../../../../../services/link-service/links';
import Global from '../../../../../../store/global-selectors';
import { FulfillmentProviderIds } from '../../../../../../enums/fulfillment-provider-ids';
import { MAX, REQUIRED } from '../../../../../../constants/yup-constants';
import { getFulfillmentServices } from '../../../../api/fulfillment-delegation';
import { settingsTabs } from '../../../constants';
import messages from '../../messages';
import { ADD, BY_WEIGHT_CALC_TYPE, SKIP_DIMENSIONS, ALL_CALC_TYPE } from '../constants';
import { DeliveryOptionsContext, getDefaultItemShape } from '../context';
import { useCalculationMethodType } from '../hooks';
import {
  pricingModelEnum,
  pricingModels,
  additionalFeesEnum,
  fallbackOptionModeEnum,
  fallbackOptions,
  fulfillmentPricingModels,
  fulfillmentServiceNamesEnum,
} from '../enums';

import { FlatRateOptions } from './flat-rate-options';
import { PudoRateOptions } from './pudo-rate-options';
import { LiveRateOptions } from './live-rate-options';
import { CalculatedRateOptions } from './calculated-rate-options';
import { deliveryOptionsFulfillmentRatesSchema, deliveryOptionsSchema } from './validation-schema';
import usePudoServicesAvailableForSeller from './use-pudo-services-available-for-seller';
import style from './edit-delivery-option-page.module.less';

const EditDeliveryOption = () => {
  const { checkoutRateId = '' } = useParams();
  const {
    addNewItemCallback,
    onUpdateCallback,
    items,
    marketplaceId,
    sharedDefaultDimensionsByWeight,
    calculationMethod,
    contextMessages,
    isDeliveryMarketplaceAuctaneFulfillment,
  } = useContext(DeliveryOptionsContext);
  const sellerDefaultDimensionUnit = useSelector(Global.selectSellerDefaultDimensionUnit);
  const sellerDefaultWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);
  const homeCountry = useSelector(Global.selectSellerHomeCountry);
  const [item, setItem] = useState(
    items.find((option) => option.id === parseInt(checkoutRateId, 10)) ||
      getDefaultItemShape(
        sellerDefaultWeightUnit,
        sellerDefaultDimensionUnit,
        homeCountry,
        sharedDefaultDimensionsByWeight,
        calculationMethod,
      ),
  );
  const [selectedPriceModel, setSelectedPriceModel] = useState(item.pricingModel);

  const pudoFeatureFlagEnabled = useEnablePudoDeliveryOptions();
  const marketplaceSupportsPudo = useSelector((state) => {
    const marketplacesReferenceData = Global.selectReferenceDatagetMarketplaces(state) || [];
    const marketplaceReferenceData = marketplacesReferenceData.find((m) => m.marketplaceId === marketplaceId);
    return marketplaceReferenceData?.hasServicePointsIntegration;
  });
  const { pudoServices, loading } = usePudoServicesAvailableForSeller(pudoFeatureFlagEnabled && marketplaceSupportsPudo);
  const shouldShowPudo = useMemo(() => {
    return pudoFeatureFlagEnabled && pudoServices.length > 0 && marketplaceSupportsPudo;
  }, [pudoFeatureFlagEnabled, pudoServices, marketplaceSupportsPudo]);
  const calcMethodType = useCalculationMethodType();
  const isLiveRatesWithDimensionsEnabled = calcMethodType !== BY_WEIGHT_CALC_TYPE && item.calculationMethod !== SKIP_DIMENSIONS;

  const [fulfillmentServices, setFulfillmentServices] = useState([]);

  useEffect(() => {
    if (isDeliveryMarketplaceAuctaneFulfillment) {
      const fetchFulfillmentServices = async () => {
        const services = await getFulfillmentServices(FulfillmentProviderIds.AuctaneFulfillment);
        setFulfillmentServices(services);
      };

      fetchFulfillmentServices();
    }
  }, [isDeliveryMarketplaceAuctaneFulfillment]);

  const intl = useIntl();
  const sellerCurrencyCode = useSelector(Global.selectSellerDefaultCurrency);
  const { pickupSettings: defaultShipFromPickupSettings } = useSelector(Global.selectSellerDefaultShipFrom);

  const sellerHasLocalDeliveryServicesAvailable = useAreLocalDeliveryServicesAvailableForSeller();

  const filteredPricingModels = (isDeliveryMarketplaceAuctaneFulfillment ? fulfillmentPricingModels : pricingModels).filter((model) => {
    if (isDeliveryMarketplaceAuctaneFulfillment) {
      return [pricingModelEnum.liveRate, pricingModelEnum.flatRate, pricingModelEnum.free].includes(model.id);
    }
    if (model.id === pricingModelEnum.localDeliveryRate) {
      return sellerHasLocalDeliveryServicesAvailable;
    }
    if (model.id === pricingModelEnum.pudo) {
      return shouldShowPudo;
    }
    return true;
  });

  const onNameInputChangeCallback = useCallback(
    (e) => {
      setItem({ ...item, name: e.target.value });
    },
    [item, setItem],
  );

  const onDescriptionInputChangeCallback = useCallback(
    (e) => {
      setItem({ ...item, description: e.target.value });
    },
    [item, setItem],
  );

  const onPricingModelChangeCallback = useCallback(
    (newPricingModel) => {
      setSelectedPriceModel(newPricingModel);
      const itemUpdate = { pricingModel: newPricingModel };
      if (
        newPricingModel === pricingModelEnum.free ||
        newPricingModel === pricingModelEnum.flatRate ||
        newPricingModel === pricingModelEnum.pudo
      ) {
        itemUpdate.services = [];
        itemUpdate.additionalFees = {
          mode: additionalFeesEnum.none,
          percentage: 0,
          flatRate: {
            value: 0,
            code: sellerCurrencyCode,
          },
        };
        itemUpdate.fallbackOption = {
          mode: fallbackOptionModeEnum.dontShow,
          cartPercentage: 0,
          flatRate: {
            value: 0,
            code: sellerCurrencyCode,
          },
        };
      }
      if (newPricingModel === pricingModelEnum.pudo) {
        itemUpdate.feeThreshold = {
          enabled: false,
          thresholdAmount: 0,
          currencyCode: sellerCurrencyCode,
        };
      }
      if (newPricingModel === pricingModelEnum.free) {
        itemUpdate.flatRate = {
          value: 0,
          code: sellerCurrencyCode,
        };
      }
      if (newPricingModel === pricingModelEnum.localDeliveryRate) {
        itemUpdate.fallbackOption = {
          mode: fallbackOptionModeEnum.dontShow,
          cartPercentage: 0,
          flatRate: {
            value: 0,
            code: sellerCurrencyCode,
          },
        };
      }
      // if the user is creating a new option or updating an option to live rate we will pre-populate these fields
      // with the values of any pre-existing option since this is shared among ALL option settings
      if (newPricingModel === pricingModelEnum.liveRate) {
        itemUpdate.defaultDimensionsByWeightList = [...sharedDefaultDimensionsByWeight];
        itemUpdate.calculationMethod = calculationMethod;
      }
      if (newPricingModel !== item.pricingModel) {
        itemUpdate.services = [];
      }
      setItem({ ...item, ...itemUpdate });
    },
    [item, sellerCurrencyCode, sharedDefaultDimensionsByWeight, calculationMethod],
  );

  const history = useHistory();
  const onSaveClickCallback = useCallback(() => {
    if (checkoutRateId === ADD) {
      addNewItemCallback(item);
    } else {
      onUpdateCallback(item);
    }
    history.push(`../${settingsTabs.checkoutRates}`);
  }, [checkoutRateId, onUpdateCallback, item, history, addNewItemCallback]);

  const onCancelClickCallback = useCallback(() => {
    history.push(`../${settingsTabs.checkoutRates}`);
  }, [history]);

  const { name, description, pricingModel, flatRate, services, additionalFees, fallbackOption, feeThreshold, hasDeactivatedService } = item;

  const serviceErrorState = useMemo(() => {
    return hasDeactivatedService && services?.length === 0 ? BannerStatus.Error : BannerStatus.Warning;
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const serviceErrorMessage = useMemo(() => {
    return hasDeactivatedService && services?.length === 0 ? messages.deactivedNoService : messages.deactivedHasOtherServicesService;
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredFulfillmentServices = fulfillmentServices.filter(
    (service) => service.serviceName !== fulfillmentServiceNamesEnum.requestedShippingMethod,
  );
  const getFulfillmentServiceName = (selectedFulfillmentServiceName) => {
    const found = filteredFulfillmentServices.find((service) => service.serviceName === selectedFulfillmentServiceName);
    return found ? found.serviceName : <FormattedMessage {...messages.selectFulfillmentService} />;
  };

  const onFulfillmentServiceChange = useCallback(
    (fulfillmentServiceName) => {
      setItem({ ...item, name: fulfillmentServiceName });
    },
    [item, setItem],
  );

  const fallbackOptionObject = fallbackOptions.find((option) => option.id === fallbackOption.mode);

  const { errors: validationErrors, isValid } = useYupFormValidation({
    form: {
      ...item,
      defaultShipFromPickupSettings,
      isLiveRatesWithDimensionsEnabled,
    },
    // Pickup Settings aren't sent in the API request, but we need to
    // validate that they exist so we know whether or not block the user
    // from saving the form. - Greg S. 11/1/21
    schema: isDeliveryMarketplaceAuctaneFulfillment ? deliveryOptionsFulfillmentRatesSchema : deliveryOptionsSchema,
  });

  const defaultFieldState = {
    nameTouched: false,
    nameFocused: false,
    descriptionTouched: false,
    descriptionFocused: false,
  };
  const [fieldState, setFieldState] = useState(defaultFieldState);
  const onBlurDeliveryOptionName = useCallback(() => {
    setFieldState((f) => ({ ...f, nameTouched: true, nameFocused: false }));
  }, [setFieldState]);
  const onFocusDeliveryOptionName = useCallback(() => {
    setFieldState((f) => ({ ...f, nameFocused: true }));
  }, [setFieldState]);

  const nameMessageType =
    validationErrors?.name?.type === MAX || (fieldState.nameTouched && validationErrors?.name?.type === REQUIRED) ? ERROR : undefined;

  const nameMessageText = () => {
    if (validationErrors?.name?.type === MAX || (fieldState.nameTouched && validationErrors?.name?.type === REQUIRED)) {
      return validationErrors?.name?.message;
    }
    if (fieldState.nameFocused) {
      return <FormattedMessage {...messages.maximum50CharactersWithRemaining} values={{ remaining: 50 - name.length }} />;
    }
    return <FormattedMessage {...messages.maximum50Characters} />;
  };

  const onBlurDeliveryOptionDescription = useCallback(() => {
    setFieldState((f) => ({
      ...f,
      descriptionTouched: true,
      descriptionFocused: false,
    }));
  }, [setFieldState]);
  const onFocusDeliveryOptionDescription = useCallback(() => {
    setFieldState((f) => ({ ...f, descriptionFocused: true }));
  }, [setFieldState]);

  const descriptionMessageType = fieldState.descriptionTouched && validationErrors?.description?.message ? ERROR : undefined;

  const getDescriptionMessageText = () => {
    if (validationErrors?.description?.message) {
      return validationErrors?.description?.message;
    }
    if (fieldState.descriptionFocused) {
      return <FormattedMessage {...messages.maximum100CharactersWithRemaining} values={{ remaining: 100 - description.length }} />;
    }
    return <FormattedMessage {...messages.maximum100Characters} />;
  };

  const namePlaceholder =
    pricingModel === pricingModelEnum.localDeliveryRate
      ? messages.checkoutRateLocalDeliveryNamePlaceholder
      : messages.checkoutRateNamePlaceholder;
  const descriptionPlaceholder =
    pricingModel === pricingModelEnum.localDeliveryRate
      ? messages.checkoutRateLocalDeliveryDescriptionPlaceholder
      : messages.checkoutRateDescriptionPlaceholder;

  const firstErrorMessage = Object.values(validationErrors)[0]?.message;
  const disabledTooltipMessage = firstErrorMessage?.defaultMessage ? <FormattedMessage {...firstErrorMessage} /> : firstErrorMessage;

  if (loading) {
    return <LoadingIndicator />;
  }

  return (
    <>
      {hasDeactivatedService && (
        <BannerMessageV2
          className={style['deactived-banner']}
          content={<Text textType={TextType.Caption} value={serviceErrorMessage} />}
          layout={BannerLayout.Full}
          status={serviceErrorState}
        />
      )}
      <div className={style.container}>
        <h2>
          <FormattedMessage {...contextMessages.defineRateTitle} />
        </h2>
        <div className={style['define-page-description']}>
          {isDeliveryMarketplaceAuctaneFulfillment ? (
            <FormattedMessage {...messages.defineFulfillmentRateDescription} />
          ) : (
            <FormattedMessage
              {...messages.defineCheckoutRateDescription}
              values={{
                br: <br />,
                a: (chunks) => (
                  <HelpArticleLink
                    articleId={
                      calcMethodType === ALL_CALC_TYPE ? linkIDs.CheckoutRatesDimensionalConfiguration : linkIDs.CheckoutRatesConfiguration
                    }
                  >
                    {chunks}
                  </HelpArticleLink>
                ),
              }}
            />
          )}
        </div>
        <div className={classnames(style['edit-delivery-options-section'])}>
          <div className={style['section-title']}>
            <FormattedMessage {...messages.rateType} />
          </div>
          <div className={classnames(style['rate-type-button-container'], style['rate-container-margin'])}>
            {filteredPricingModels.map((model) => (
              <Button
                key={model.id}
                className={classnames({
                  [style.selected]: selectedPriceModel === model.id,
                })}
                onClick={() => onPricingModelChangeCallback(model.id)}
                theme='white'
              >
                <div className={style['rate-type-name']}>{model.displayName}</div>
                <div className={style['rate-type-description']}>{model.description}</div>
                {selectedPriceModel === model.id && <FontAwesomeIcon className={style.check} icon={faCheck} />}
              </Button>
            ))}
          </div>
        </div>
        {!!pricingModel && (
          <div className={['section-container']}>
            <div className={classnames(style['edit-delivery-options-section'], style['section-container'])}>
              <div className={classnames(style['section-title'])}>
                <Text
                  className={style['card-section-title']}
                  textType={TextType.Paragraph}
                  value={messages.nameAndDescriptionSectionTitle}
                />
                <Text className={style['section-info']} textType={TextType.Paragraph} value={messages.checkoutRateTitleSubHeader} />
                <Text
                  className={{ [style.error]: nameMessageType === ERROR }}
                  textType={TextType.Label}
                  value={(() => {
                    if (pricingModel === pricingModelEnum.pudo) {
                      return messages.pudoCheckoutRateTitle;
                    }
                    if (isDeliveryMarketplaceAuctaneFulfillment) {
                      return messages.fulfillmentRateTitle;
                    }
                    return messages.checkoutRateInputTitle;
                  })()}
                />
              </div>
              {isDeliveryMarketplaceAuctaneFulfillment ? (
                <div className={style['section-controls']}>
                  <DropdownMenu dropdownMenuShouldHaveWidthOfButton fullWidth value={name} variant='form'>
                    <DropdownMenuToggler>{getFulfillmentServiceName(name)}</DropdownMenuToggler>
                    {filteredFulfillmentServices.map(({ serviceId: fulfillmentServiceId, serviceName: fulfillmentServiceName }) => (
                      <DropdownMenuItem
                        key={fulfillmentServiceId}
                        onClick={() => onFulfillmentServiceChange(fulfillmentServiceName)}
                        selected={name === fulfillmentServiceName}
                      >
                        {fulfillmentServiceName}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenu>
                </div>
              ) : (
                <>
                  <div className={style['section-controls']}>
                    <Input
                      inlineMessage={nameMessageText()}
                      inlineMessageType={nameMessageType}
                      onBlur={onBlurDeliveryOptionName}
                      onChange={onNameInputChangeCallback}
                      onFocus={onFocusDeliveryOptionName}
                      placeholder={intl.formatMessage(namePlaceholder)}
                      value={name}
                      version={version2}
                    />
                  </div>
                  <Text
                    className={classnames(style['section-title'], {
                      [style.error]: descriptionMessageType === ERROR,
                    })}
                    textType={TextType.Label}
                    value={contextMessages.deliveryOptionDescription}
                  />
                  <div className={classnames(style['section-controls'], style.description)}>
                    <Input
                      inlineMessage={getDescriptionMessageText()}
                      inlineMessageType={descriptionMessageType}
                      onBlur={onBlurDeliveryOptionDescription}
                      onChange={onDescriptionInputChangeCallback}
                      onFocus={onFocusDeliveryOptionDescription}
                      placeholder={intl.formatMessage(descriptionPlaceholder)}
                      value={description}
                      version={version2}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {pricingModel === pricingModelEnum.flatRate && <FlatRateOptions flatRateValue={flatRate.value} item={item} setItem={setItem} />}
        {pricingModel === pricingModelEnum.liveRate && !isDeliveryMarketplaceAuctaneFulfillment && (
          <LiveRateOptions
            additionalFees={additionalFees}
            defaultDimensionsByWeight={sharedDefaultDimensionsByWeight}
            fallbackOption={fallbackOption}
            fallbackOptionObject={fallbackOptionObject}
            fallbackOptions={fallbackOptions}
            item={item}
            pricingMode={pricingModelEnum.liveRate}
            services={services}
            setItem={setItem}
            validationErrors={validationErrors}
          />
        )}
        {pricingModel === pricingModelEnum.liveRate && isDeliveryMarketplaceAuctaneFulfillment && (
          <CalculatedRateOptions additionalFees={additionalFees} item={item} setItem={setItem} />
        )}
        {pricingModel === pricingModelEnum.localDeliveryRate && (
          <LiveRateOptions
            additionalFees={additionalFees}
            item={item}
            pricingMode={pricingModelEnum.localDeliveryRate}
            services={services}
            setItem={setItem}
            validationErrors={validationErrors}
          />
        )}
        {pricingModel === pricingModelEnum.pudo && (
          <PudoRateOptions
            feeThresholdValue={feeThreshold.thresholdAmount}
            flatRateValue={flatRate.value}
            item={item}
            pudoServices={pudoServices}
            services={services}
            setItem={setItem}
            validationErrors={validationErrors}
          />
        )}
        {!!pricingModel && (
          <div className={style.footer}>
            <hr />
            <Button
              disabled={!isValid}
              disabledTooltipMessage={disabledTooltipMessage}
              onClick={onSaveClickCallback}
              pendoTrackingId='/settings/stores#save-delivery-option'
              theme='primary'
            >
              <FormattedMessage {...messages.editOptionSaveRate} />
            </Button>
            <Button onClick={onCancelClickCallback} theme='tertiary'>
              <FormattedMessage {...messages.editOptionCancel} />
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

const EditDeliveryOptionsWrapper = (props) => {
  const { loading } = useContext(DeliveryOptionsContext);

  if (loading) {
    return <LoadingIndicator />;
  }
  return <EditDeliveryOption {...props} />;
};

export default EditDeliveryOptionsWrapper;
